import { Component, Inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { BaseModalComponent } from "../base-modal/base-modal.component";
import { MobileService } from "../../services/mobile.service";

/** One live announcement's worth of content - see UpdateCompleteModalService. Each list
 * is independent and only renders when it has at least one entry. */
export interface DialogUpdateCompleteAnnouncement {
  version: string;
  newFeatures: string[];
  bugFixes: string[];
}

/** FLIP-79, extended by FLIP-332 to accumulate every version a user has skipped rather
 * than just the newest. `announcements` is always non-empty and ordered oldest first;
 * `latestVersion` is the highest version among them, shown in the modal's opening line. */
export interface DialogUpdateCompleteData {
  latestVersion: string;
  announcements: DialogUpdateCompleteAnnouncement[];
  /** FLIP-563: any displayed announcement needs the iPhone / Android app updated. */
  requiresIosUpdate?: boolean;
  requiresAndroidUpdate?: boolean;
}

/**
 * "You're up to date!" / What's New modal, shown once per version per user (see
 * UpdateCompleteModalService for the seen-tracking). Built directly on app-base-modal,
 * same approach as FLIP-78's dialog-refresh.component.ts, for the Figma-specified
 * sundown -> parchment gradient and wisteria Close pill that the fixed
 * ConfirmationModalComponent wrapper doesn't support.
 */
@Component({
  selector: "app-dialog-update-complete",
  templateUrl: "./dialog-update-complete.component.html",
  styleUrls: ["./dialog-update-complete.component.scss"],
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
})
export class DialogUpdateCompleteComponent {
  /** FLIP-563: which mobile-update note to show, if any. Only the app the user is actually
   * running matters - an iPhone-only release shows nothing to an Android user, and nothing
   * shows in a plain browser, where there is no mobile app to update. */
  readonly mobileUpdateNote: "ios" | "android" | null;

  constructor(
    public dialogRef: MatDialogRef<DialogUpdateCompleteComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogUpdateCompleteData,
    mobileService: MobileService
  ) {
    const platform = mobileService.getPlatformType();
    if (platform === mobileService.IOS && data.requiresIosUpdate) {
      this.mobileUpdateNote = "ios";
    } else if (platform === mobileService.ANDROID && data.requiresAndroidUpdate) {
      this.mobileUpdateNote = "android";
    } else {
      this.mobileUpdateNote = null;
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
